import { useState, useEffect } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { CheckCircle2, AlertCircle, Copy, Loader2, Zap, Shield } from "lucide-react";
import { toast } from "sonner";

export function MicrosoftLoginSetup({ integration }) {
  const API = process.env.REACT_APP_BACKEND_URL + "/api";
  const [activeTab, setActiveTab] = useState("guide");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [config, setConfig] = useState({ credentials: {}, redirect_uri: "", status: "not_configured" });

  useEffect(() => {
    const fetchConfig = async () => {
      try {
        const res = await axios.get(`${API}/integrations/microsoft-login-config`);
        setConfig(res.data);
      } catch (e) { console.error(e); }
      setLoading(false);
    };
    fetchConfig();
  }, [API]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await axios.put(`${API}/integrations/microsoft-login-config`, { credentials: config.credentials });
      setConfig(prev => ({ ...prev, status: res.data.status, redirect_uri: res.data.redirect_uri }));
      toast.success("Microsoft Login configuration saved!");
    } catch { toast.error("Failed to save configuration"); }
    setSaving(false);
  };

  const handleTest = async () => {
    setTesting(true);
    try {
      const res = await axios.post(`${API}/integrations/microsoft-login-config/test`);
      if (res.data.success) toast.success(res.data.message);
      else toast.error(res.data.message);
    } catch { toast.error("Test connection failed"); }
    setTesting(false);
  };

  const updateCred = (key, value) => {
    setConfig(prev => ({ ...prev, credentials: { ...prev.credentials, [key]: value } }));
  };

  if (loading) return <div className="flex justify-center py-8"><Loader2 className="w-5 h-5 animate-spin text-[#00A4EF]" /></div>;

  return (
    <div className="space-y-4" data-testid="microsoft-login-setup">
      <div className="flex gap-1 p-0.5 bg-gray-100 rounded-lg">
        {[{id:"guide",label:"Setup Guide"},{id:"credentials",label:"Credentials"},{id:"status",label:"Status"}].map(t => (
          <button key={t.id} onClick={() => setActiveTab(t.id)} className={`flex-1 py-1.5 px-3 text-xs font-medium rounded-md transition-colors ${activeTab === t.id ? "bg-white text-[#00A4EF] shadow-sm" : "text-gray-500 hover:text-gray-700"}`} data-testid={`ms-login-tab-${t.id}`}>
            {t.label}
          </button>
        ))}
      </div>

      {activeTab === "guide" && (
        <div className="space-y-4">
          <div className="p-4 bg-[#00A4EF]/5 border border-[#00A4EF]/15 rounded-xl">
            <h4 className="font-semibold text-sm text-gray-900 mb-3">Azure AD App Registration — Step by Step</h4>
            <ol className="space-y-4 text-sm text-gray-700">
              <li className="flex gap-2">
                <span className="font-bold text-[#00A4EF] shrink-0">1.</span>
                <div>
                  <p className="font-medium">Go to Azure Portal</p>
                  <p className="text-xs text-gray-500 mt-0.5">Open <a href="https://portal.azure.com/#view/Microsoft_AAD_RegisteredApps/ApplicationsListBlade" target="_blank" rel="noopener noreferrer" className="text-[#00A4EF] underline">Azure Portal &gt; App Registrations</a></p>
                </div>
              </li>
              <li className="flex gap-2">
                <span className="font-bold text-[#00A4EF] shrink-0">2.</span>
                <div>
                  <p className="font-medium">Register a New Application</p>
                  <p className="text-xs text-gray-500 mt-0.5">Click <strong>"New registration"</strong>. Name it (e.g., "POMEN Lead Tracker Login"). Under <strong>"Supported account types"</strong>, select <strong>"Accounts in this organizational directory only"</strong> (single tenant).</p>
                </div>
              </li>
              <li className="flex gap-2">
                <span className="font-bold text-[#00A4EF] shrink-0">3.</span>
                <div>
                  <p className="font-medium">Set the Redirect URI</p>
                  <p className="text-xs text-gray-500 mt-0.5">Select platform <strong>"Web"</strong> and enter this Redirect URI:</p>
                  <div className="mt-1.5 flex items-center gap-2">
                    <code className="bg-white px-3 py-1.5 rounded-lg border border-gray-200 text-xs font-mono flex-1 break-all">{config.redirect_uri || "Loading..."}</code>
                    <button onClick={() => { navigator.clipboard.writeText(config.redirect_uri); toast.success("Copied!"); }} className="shrink-0 p-1.5 rounded-lg border border-gray-200 hover:bg-gray-50"><Copy className="w-3.5 h-3.5 text-gray-500" /></button>
                  </div>
                </div>
              </li>
              <li className="flex gap-2">
                <span className="font-bold text-[#00A4EF] shrink-0">4.</span>
                <div>
                  <p className="font-medium">Copy Application (client) ID</p>
                  <p className="text-xs text-gray-500 mt-0.5">After registration, go to <strong>"Overview"</strong>. Copy the <strong>"Application (client) ID"</strong> and <strong>"Directory (tenant) ID"</strong>.</p>
                </div>
              </li>
              <li className="flex gap-2">
                <span className="font-bold text-[#00A4EF] shrink-0">5.</span>
                <div>
                  <p className="font-medium">Create a Client Secret</p>
                  <p className="text-xs text-gray-500 mt-0.5">Go to <strong>"Certificates & secrets"</strong> &gt; <strong>"New client secret"</strong>. Add a description and expiry. Copy the <strong>"Value"</strong> immediately (it won't be shown again).</p>
                </div>
              </li>
              <li className="flex gap-2">
                <span className="font-bold text-[#00A4EF] shrink-0">6.</span>
                <div>
                  <p className="font-medium">Configure ID Token</p>
                  <p className="text-xs text-gray-500 mt-0.5">Go to <strong>"Authentication"</strong> &gt; under <strong>"Implicit grant and hybrid flows"</strong>, check <strong>"ID tokens"</strong>. Click Save.</p>
                </div>
              </li>
              <li className="flex gap-2">
                <span className="font-bold text-[#00A4EF] shrink-0">7.</span>
                <div>
                  <p className="font-medium">Add Optional Email Claim</p>
                  <p className="text-xs text-gray-500 mt-0.5">Go to <strong>"Token configuration"</strong> &gt; <strong>"Add optional claim"</strong> &gt; Select <strong>"ID"</strong> token type &gt; Check <strong>"email"</strong> &gt; Add. This ensures the email is included in the token.</p>
                </div>
              </li>
              <li className="flex gap-2">
                <span className="font-bold text-[#00A4EF] shrink-0">8.</span>
                <div>
                  <p className="font-medium">Enter Credentials</p>
                  <p className="text-xs text-gray-500 mt-0.5">Go to the <strong>"Credentials"</strong> tab above and paste your Tenant ID, Client ID, and Client Secret. Then click <strong>"Test Connection"</strong>.</p>
                </div>
              </li>
            </ol>
          </div>

          <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg text-xs text-blue-800 flex items-start gap-2">
            <Shield className="w-4 h-4 shrink-0 mt-0.5" />
            <span><strong>How it works:</strong> Team members can click "Sign in with Microsoft" on the login page. Their Microsoft email is matched to their POMEN account. They must already exist in the Team page — Microsoft login does not create new accounts.</span>
          </div>
        </div>
      )}

      {activeTab === "credentials" && (
        <div className="space-y-4">
          <div className="space-y-3">
            <div>
              <Label className="text-xs text-gray-500">Directory (Tenant) ID *</Label>
              <Input value={config.credentials.tenant_id || ""} onChange={(e) => updateCred("tenant_id", e.target.value)} placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx" className="border-gray-300 rounded-lg text-sm font-mono" data-testid="ms-login-tenant-id" />
              <p className="text-[10px] text-gray-400 mt-0.5">Found in Azure Portal &gt; App Registration &gt; Overview</p>
            </div>
            <div>
              <Label className="text-xs text-gray-500">Application (Client) ID *</Label>
              <Input value={config.credentials.client_id || ""} onChange={(e) => updateCred("client_id", e.target.value)} placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx" className="border-gray-300 rounded-lg text-sm font-mono" data-testid="ms-login-client-id" />
            </div>
            <div>
              <Label className="text-xs text-gray-500">Client Secret *</Label>
              <Input type="password" value={config.credentials.client_secret || ""} onChange={(e) => updateCred("client_secret", e.target.value)} placeholder="Your client secret value" className="border-gray-300 rounded-lg text-sm font-mono" data-testid="ms-login-client-secret" />
              <p className="text-[10px] text-gray-400 mt-0.5">The secret Value (not Secret ID)</p>
            </div>
          </div>

          <div className="space-y-2">
            <Label className="text-xs text-gray-500">Redirect URI (auto-configured)</Label>
            <div className="flex items-center gap-2">
              <code className="flex-1 bg-gray-50 px-3 py-2 rounded-lg border border-gray-200 text-xs font-mono break-all">{config.redirect_uri || "Loading..."}</code>
              <button onClick={() => { navigator.clipboard.writeText(config.redirect_uri); toast.success("Copied!"); }} className="shrink-0 p-2 rounded-lg border border-gray-200 hover:bg-gray-50"><Copy className="w-3.5 h-3.5 text-gray-500" /></button>
            </div>
            <p className="text-[10px] text-gray-400">Add this URL in Azure &gt; App Registration &gt; Authentication &gt; Redirect URIs</p>
          </div>

          <div className="flex gap-2">
            <Button onClick={handleTest} disabled={testing} variant="outline" className="flex-1 border-[#00A4EF] text-[#00A4EF] hover:bg-[#00A4EF]/5 rounded-lg" data-testid="ms-login-test-btn">
              {testing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Zap className="w-4 h-4 mr-2" />} Test Connection
            </Button>
            <Button onClick={handleSave} disabled={saving} className="flex-1 bg-[#00A4EF] hover:bg-[#0090D4] text-white rounded-lg" data-testid="ms-login-save-btn">
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CheckCircle2 className="w-4 h-4 mr-2" />} Save
            </Button>
          </div>
        </div>
      )}

      {activeTab === "status" && (
        <div className="space-y-4">
          <div className="p-4 bg-gray-50 rounded-xl border border-gray-200 space-y-3">
            <h5 className="text-sm font-semibold text-gray-900">Connection Status</h5>
            <div className="grid gap-2 text-xs">
              <div className="flex justify-between items-center">
                <span className="text-gray-500">Status:</span>
                {config.status === "connected" ? (
                  <span className="text-emerald-600 font-medium flex items-center gap-1"><CheckCircle2 className="w-3 h-3" /> Connected</span>
                ) : config.using_env ? (
                  <span className="text-blue-600 font-medium flex items-center gap-1"><CheckCircle2 className="w-3 h-3" /> Using Environment Config</span>
                ) : (
                  <span className="text-amber-600 font-medium flex items-center gap-1"><AlertCircle className="w-3 h-3" /> Not Configured</span>
                )}
              </div>
              <div className="flex justify-between items-center">
                <span className="text-gray-500">Tenant ID:</span>
                <code className="bg-white px-2 py-1 rounded border font-mono text-xs">{config.credentials?.tenant_id ? "****" + config.credentials.tenant_id.slice(-8) : (config.using_env ? "From env" : "Not set")}</code>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-gray-500">Client ID:</span>
                <code className="bg-white px-2 py-1 rounded border font-mono text-xs">{config.credentials?.client_id ? "****" + config.credentials.client_id.slice(-8) : (config.using_env ? "From env" : "Not set")}</code>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-gray-500">Redirect URI:</span>
                <code className="bg-white px-2 py-1 rounded border font-mono text-[10px] max-w-[200px] truncate">{config.redirect_uri}</code>
              </div>
            </div>
          </div>

          <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg text-xs text-amber-800 flex items-start gap-2">
            <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
            <span><strong>Important:</strong> The Redirect URI above must be registered in your Azure App Registration under Authentication &gt; Web &gt; Redirect URIs. Microsoft will reject the login if this URI is not added.</span>
          </div>
        </div>
      )}
    </div>
  );
}
